import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, BatteryCharging, Building2, CheckCircle2, Factory, Network, RefreshCw, ShieldCheck, Timer, Wrench, Zap } from 'lucide-react';
import { EnPowerWordmarkInline } from '../../EnPowerLogo';

const STORAGE_KEY = 'enpower-energy-flyers-v1';

const DEFAULT_FLYERS = {
  bess: {
    label: 'BESS',
    tabLabel: 'BESS',
    eyebrow: 'BESS · Großspeicher',
    headline: 'BESS-GROSSSPEICHER\nFÜR IHR GRUNDSTÜCK',
    subline: 'EN:POWER entwickelt, finanziert und betreibt Batteriespeicher als langfristiges Infrastrukturprojekt - mit planbaren Standorterlösen und ohne Investition für Eigentümer.',
    badge: 'Großspeicher · Projektentwicklung · Betrieb aus einer Hand',
    image: '/bess-grossspeicher.png',
    imageAlt: 'BESS-Großspeicher',
    introTitle: 'Wofür BESS eingesetzt wird',
    introText: 'BESS-Anlagen speichern Strom, stabilisieren das Netz und schaffen flexible Kapazitäten für die Energiewende. Geeignete Grundstücke werden als technische Infrastrukturstandorte genutzt, während EN:POWER Planung, Finanzierung, Errichtung und Betrieb übernimmt.',
    offerTitle: 'Ihr Vorteil als Standortpartner',
    offerText: 'Aus einer ungenutzten Fläche entsteht ein langfristig nutzbarer Energie-Infrastrukturstandort. Die konkrete Vergütung hängt von Netzanschluss, Flächengröße, Genehmigungsfähigkeit und Projektdimension ab.',
    ctaTitle: 'Standort prüfen lassen',
    ctaText: 'Wir bewerten Netzanschluss, Fläche, Zufahrt und Genehmigungsrahmen und melden uns mit einer realistischen Ersteinschätzung.',
    contact: 'info@enpower.de · enpower.de',
    metrics: [
      { label: 'Laufzeit', value: '20-30 Jahre', note: 'langfristiger Standortvertrag' },
      { label: 'Fläche', value: 'ab ca. 300 m²', note: 'abhängig von Leistung und Layout' },
      { label: 'Investition', value: '0 EUR', note: 'Projektkosten trägt EN:POWER' },
      { label: 'Betrieb', value: '100 %', note: 'Planung, Bau und Monitoring durch uns' },
    ],
    benefits: [
      { title: 'Planbare Standorterlöse', text: 'Langfristige Einnahmen für geeignete Grundstücke statt ungenutzter Flächenreserve.' },
      { title: 'Technische Umsetzung', text: 'EN:POWER steuert Planung, Netzanschluss, Genehmigung, Bau, Betrieb und Wartung.' },
      { title: 'Netzdienlicher Betrieb', text: 'Batteriespeicher unterstützen Frequenzhaltung, Lastmanagement und Flexibilität im Stromsystem.' },
      { title: 'Skalierbares Projekt', text: 'Die finale Dimensionierung richtet sich nach Netzkapazität, Standort und wirtschaftlicher Machbarkeit.' },
    ],
    process: ['Standortanalyse', 'Netzprüfung', 'Nutzungsvertrag', 'Genehmigung', 'Bau & Betrieb'],
  },
  schwarmspeicher: {
    label: 'Schwarmspeicher',
    tabLabel: 'Schwarmspeicher',
    eyebrow: 'Schwarmspeicher · Kompakte Batteriespeicher',
    headline: 'SCHWARMSPEICHER\nAUF KLEINER FLÄCHE',
    subline: 'Kompakte Speichereinheiten werden zu einem virtuellen Speicherverbund zusammengeführt. So entstehen zusätzliche Erlöse auf sehr kleiner Stellfläche - ohne Investitionskosten für Eigentümer.',
    badge: 'Kleine Stellfläche · schnelle Umsetzung · skalierbarer Speicherverbund',
    image: '/speicher-unit.jpeg',
    imageAlt: 'EN:POWER Schwarmspeicher Einheit',
    introTitle: 'Warum ein Schwarmspeicher passt',
    introText: 'Schwarmspeicher eignen sich für Gewerbeflächen, Parkplätze, Randflächen und technische Nebenflächen. Mehrere Einheiten können gemeinsam netzdienliche Leistungen erbringen, ohne große Flächen zu blockieren.',
    offerTitle: 'Kompakt, wirtschaftlich, betreut',
    offerText: 'EN:POWER übernimmt Standortprüfung, Netzbetreiberkommunikation, Aufstellung, Betrieb, Wartung, Monitoring und Versicherung. Für Eigentümer entstehen keine Anschaffungs- oder Betriebskosten.',
    ctaTitle: 'Stellfläche bewerten lassen',
    ctaText: 'Wir prüfen, ob Netzanschluss, Zugang und Fläche für ein Schwarmspeicherprojekt geeignet sind.',
    contact: 'info@enpower.de · enpower.de',
    metrics: [
      { label: 'Laufzeit', value: '20-30 Jahre', note: 'langfristige Standortnutzung' },
      { label: 'Stellfläche', value: 'ab ca. 12 m²', note: 'inklusive Sicherheitsabstände' },
      { label: 'Investition', value: '0 EUR', note: 'keine Kosten für Eigentümer' },
      { label: 'Umsetzung', value: '4-8 Monate', note: 'nach positiver Netzrückmeldung' },
    ],
    benefits: [
      { title: 'Minimale Fläche', text: 'Auch kleine technische Restflächen können wirtschaftlich nutzbar werden.' },
      { title: 'Schneller Projektstart', text: 'Standardisierte Einheiten verkürzen Planung, Aufbau und Betriebsvorbereitung.' },
      { title: 'Komplett betreut', text: 'EN:POWER übernimmt Netzprüfung, Installation, Wartung, Versicherung und Monitoring.' },
      { title: 'Erweiterbar', text: 'Bei Netzkapazität kann der Speicherverbund schrittweise skaliert werden.' },
    ],
    process: ['Standortanalyse', 'Angebot', 'Nutzungsvertrag', 'Netzbetreiber', 'Bau & Betrieb'],
  },
};

const FLYER_KEYS = ['bess', 'schwarmspeicher'];

const metricIcons = [Timer, Building2, Zap, ShieldCheck];
const benefitIcons = [CheckCircle2, Wrench, Network, BatteryCharging];
const processIcons = [Factory, Network, CheckCircle2, Wrench, Zap];

const cloneDefaultFlyers = () => JSON.parse(JSON.stringify(DEFAULT_FLYERS));

const mergeFlyerData = (fallbackData, storedData) => {
  if (!storedData || typeof storedData !== 'object') {
    return fallbackData;
  }

  const nextData = { ...fallbackData, ...storedData };
  nextData.metrics = fallbackData.metrics.map((metric, metricIndex) => ({
    ...metric,
    ...(Array.isArray(storedData.metrics) ? storedData.metrics[metricIndex] : {}),
  }));
  nextData.benefits = fallbackData.benefits.map((benefit, benefitIndex) => ({
    ...benefit,
    ...(Array.isArray(storedData.benefits) ? storedData.benefits[benefitIndex] : {}),
  }));
  nextData.process = Array.isArray(storedData.process) && storedData.process.length === fallbackData.process.length
    ? storedData.process
    : fallbackData.process;

  return nextData;
};

const normalizeFlyers = (storedContent) => {
  const fallbackContent = cloneDefaultFlyers();

  return Object.fromEntries(
    FLYER_KEYS.map((flyerKey) => [flyerKey, mergeFlyerData(fallbackContent[flyerKey], storedContent?.[flyerKey])])
  );
};

const loadStoredFlyers = () => {
  if (typeof window === 'undefined') {
    return cloneDefaultFlyers();
  }

  try {
    const rawContent = window.localStorage.getItem(STORAGE_KEY);
    return rawContent ? normalizeFlyers(JSON.parse(rawContent)) : cloneDefaultFlyers();
  } catch (error) {
    console.warn('Flyer-Inhalte konnten nicht geladen werden.', error);
    return cloneDefaultFlyers();
  }
};

const persistFlyers = (flyers) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flyers));
};

const updateArrayEntry = (items, entryIndex, fieldName, value) => items.map((item, currentIndex) => (
  currentIndex === entryIndex ? { ...item, [fieldName]: value } : item
));

const updateStringEntry = (items, entryIndex, value) => items.map((item, currentIndex) => (
  currentIndex === entryIndex ? value : item
));

const EditorTextField = ({ label, value, multiline = false, onChange, disabled = false }) => (
  <label className="flyer-editor-field">
    <span>{label}</span>
    {multiline ? (
      <textarea value={value} rows={4} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    ) : (
      <input type="text" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    )}
  </label>
);

const FlyerEditorPanel = ({ activeFlyerKey, flyers, canEdit, onSelectFlyer, onFieldChange, onMetricChange, onBenefitChange, onProcessChange, onReset }) => {
  const flyer = flyers[activeFlyerKey];

  return (
    <aside className="flyer-editor-panel" data-print-hidden="true">
      <div className="flyer-editor-panel-header">
        <div>
          <p>Flyer</p>
          <h2>BESS & Schwarmspeicher</h2>
        </div>
        <button type="button" className="btn-docx flyer-reset-button" onClick={onReset} disabled={!canEdit} title="Flyer-Inhalte zurücksetzen">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="flyer-editor-tabs">
        {FLYER_KEYS.map((flyerKey) => (
          <button
            type="button"
            key={flyerKey}
            className={activeFlyerKey === flyerKey ? 'active' : ''}
            disabled={!canEdit}
            onClick={() => onSelectFlyer(flyerKey)}
          >
            {flyers[flyerKey].tabLabel}
          </button>
        ))}
      </div>

      <div className="flyer-editor-scroll">
        <section className="flyer-editor-section">
          <h3>Grunddaten</h3>
          <EditorTextField disabled={!canEdit} label="Eyebrow" value={flyer.eyebrow} onChange={(value) => onFieldChange(activeFlyerKey, 'eyebrow', value)} />
          <EditorTextField disabled={!canEdit} label="Titel" value={flyer.headline} multiline onChange={(value) => onFieldChange(activeFlyerKey, 'headline', value)} />
          <EditorTextField disabled={!canEdit} label="Untertitel" value={flyer.subline} multiline onChange={(value) => onFieldChange(activeFlyerKey, 'subline', value)} />
          <EditorTextField disabled={!canEdit} label="Badge" value={flyer.badge} onChange={(value) => onFieldChange(activeFlyerKey, 'badge', value)} />
          <EditorTextField disabled={!canEdit} label="Bildpfad" value={flyer.image} onChange={(value) => onFieldChange(activeFlyerKey, 'image', value)} />
        </section>

        <section className="flyer-editor-section">
          <h3>Kennzahlen</h3>
          {flyer.metrics.map((metric, metricIndex) => (
            <div className="flyer-editor-group" key={`${activeFlyerKey}-metric-${metricIndex}`}>
              <EditorTextField disabled={!canEdit} label="Label" value={metric.label} onChange={(value) => onMetricChange(activeFlyerKey, metricIndex, 'label', value)} />
              <EditorTextField disabled={!canEdit} label="Wert" value={metric.value} onChange={(value) => onMetricChange(activeFlyerKey, metricIndex, 'value', value)} />
              <EditorTextField disabled={!canEdit} label="Hinweis" value={metric.note} onChange={(value) => onMetricChange(activeFlyerKey, metricIndex, 'note', value)} />
            </div>
          ))}
        </section>

        <section className="flyer-editor-section">
          <h3>Inhalt</h3>
          <EditorTextField disabled={!canEdit} label="Intro Überschrift" value={flyer.introTitle} onChange={(value) => onFieldChange(activeFlyerKey, 'introTitle', value)} />
          <EditorTextField disabled={!canEdit} label="Intro Text" value={flyer.introText} multiline onChange={(value) => onFieldChange(activeFlyerKey, 'introText', value)} />
          <EditorTextField disabled={!canEdit} label="Angebot Überschrift" value={flyer.offerTitle} onChange={(value) => onFieldChange(activeFlyerKey, 'offerTitle', value)} />
          <EditorTextField disabled={!canEdit} label="Angebot Text" value={flyer.offerText} multiline onChange={(value) => onFieldChange(activeFlyerKey, 'offerText', value)} />
        </section>

        <section className="flyer-editor-section">
          <h3>Vorteile</h3>
          {flyer.benefits.map((benefit, benefitIndex) => (
            <div className="flyer-editor-group" key={`${activeFlyerKey}-benefit-${benefitIndex}`}>
              <EditorTextField disabled={!canEdit} label="Titel" value={benefit.title} onChange={(value) => onBenefitChange(activeFlyerKey, benefitIndex, 'title', value)} />
              <EditorTextField disabled={!canEdit} label="Text" value={benefit.text} multiline onChange={(value) => onBenefitChange(activeFlyerKey, benefitIndex, 'text', value)} />
            </div>
          ))}
        </section>

        <section className="flyer-editor-section">
          <h3>Ablauf</h3>
          {flyer.process.map((processStep, processIndex) => (
            <EditorTextField
              key={`${activeFlyerKey}-process-${processIndex}`}
              label={`Schritt ${processIndex + 1}`}
              value={processStep}
              disabled={!canEdit}
              onChange={(value) => onProcessChange(activeFlyerKey, processIndex, value)}
            />
          ))}
        </section>

        <section className="flyer-editor-section">
          <h3>CTA</h3>
          <EditorTextField disabled={!canEdit} label="CTA Titel" value={flyer.ctaTitle} onChange={(value) => onFieldChange(activeFlyerKey, 'ctaTitle', value)} />
          <EditorTextField disabled={!canEdit} label="CTA Text" value={flyer.ctaText} multiline onChange={(value) => onFieldChange(activeFlyerKey, 'ctaText', value)} />
          <EditorTextField disabled={!canEdit} label="Kontakt" value={flyer.contact} onChange={(value) => onFieldChange(activeFlyerKey, 'contact', value)} />
        </section>
      </div>
    </aside>
  );
};

const FlyerMetric = ({ metric, metricIndex }) => {
  const MetricIcon = metricIcons[metricIndex] || Zap;

  return (
    <div className="energy-flyer-metric">
      <div className="energy-flyer-metric-icon"><MetricIcon size={16} /></div>
      <p>{metric.label}</p>
      <strong>{metric.value}</strong>
      <span>{metric.note}</span>
    </div>
  );
};

const BenefitItem = ({ benefit, benefitIndex }) => {
  const BenefitIcon = benefitIcons[benefitIndex] || CheckCircle2;

  return (
    <div className="energy-flyer-benefit">
      <div><BenefitIcon size={15} /></div>
      <h4>{benefit.title}</h4>
      <p>{benefit.text}</p>
    </div>
  );
};

const ProcessStep = ({ label, processIndex }) => {
  const ProcessIcon = processIcons[processIndex] || ArrowRight;

  return (
    <li>
      <span>{String(processIndex + 1).padStart(2, '0')}</span>
      <div><ProcessIcon size={14} /></div>
      <p>{label}</p>
    </li>
  );
};

const FlyerPage = ({ flyer, flyerKey, pageNumber }) => (
  <article className={`a4-page energy-flyer-page energy-flyer-page-${flyerKey}`} data-testid={`flyer-page-${flyerKey}`}>
    <header className="energy-flyer-hero">
      <div className="energy-flyer-hero-copy">
        <div className="energy-flyer-brand-row">
          <EnPowerWordmarkInline className="energy-flyer-wordmark" enColor="#FFFFFF" powerColor="#E07000" fontFamily="Outfit, sans-serif" fontSize="20px" fontWeight={800} letterSpacing="0" />
          <span className="energy-flyer-eyebrow">{flyer.eyebrow}</span>
        </div>
        <h1>{flyer.headline}</h1>
        <p>{flyer.subline}</p>
        <div className="energy-flyer-badge"><BatteryCharging size={15} />{flyer.badge}</div>
      </div>
      <figure className="energy-flyer-visual">
        <img src={flyer.image} alt={flyer.imageAlt} className="energy-flyer-product-image" loading="eager" />
      </figure>
    </header>

    <section className="energy-flyer-metrics">
      {flyer.metrics.map((metric, metricIndex) => (
        <FlyerMetric key={`${flyerKey}-metric-${metricIndex}`} metric={metric} metricIndex={metricIndex} />
      ))}
    </section>

    <section className="energy-flyer-content-grid">
      <div className="energy-flyer-main-copy">
        <span className="energy-flyer-overline">EN:POWER Infrastruktur</span>
        <h2>{flyer.introTitle}</h2>
        <p>{flyer.introText}</p>

        <div className="energy-flyer-benefits">
          {flyer.benefits.map((benefit, benefitIndex) => (
            <BenefitItem key={`${flyerKey}-benefit-${benefitIndex}`} benefit={benefit} benefitIndex={benefitIndex} />
          ))}
        </div>
      </div>

      <aside className="energy-flyer-side-panel">
        <div className="energy-flyer-side-block">
          <span className="energy-flyer-overline">Projektablauf</span>
          <ol>
            {flyer.process.map((processStep, processIndex) => (
              <ProcessStep key={`${flyerKey}-process-${processIndex}`} label={processStep} processIndex={processIndex} />
            ))}
          </ol>
        </div>
        <div className="energy-flyer-offer-box">
          <h3>{flyer.offerTitle}</h3>
          <p>{flyer.offerText}</p>
        </div>
      </aside>
    </section>

    <footer className="energy-flyer-footer">
      <div>
        <h3>{flyer.ctaTitle}</h3>
        <p>{flyer.ctaText}</p>
      </div>
      <div className="energy-flyer-footer-contact">
        <EnPowerWordmarkInline className="energy-flyer-wordmark" enColor="#122A5C" powerColor="#E07000" fontFamily="Outfit, sans-serif" fontSize="15px" fontWeight={800} letterSpacing="0" />
        <span>{flyer.contact}</span>
        <small>Seite {pageNumber}</small>
      </div>
    </footer>
  </article>
);

export const EnergyFlyerStudio = ({ pagesRef, canEdit = true }) => {
  const [flyers, setFlyers] = useState(() => loadStoredFlyers());
  const [activeFlyerKey, setActiveFlyerKey] = useState('bess');

  useEffect(() => {
    persistFlyers(flyers);
  }, [flyers]);

  const handleFieldChange = useCallback((flyerKey, fieldName, value) => {
    setFlyers((currentFlyers) => ({
      ...currentFlyers,
      [flyerKey]: {
        ...currentFlyers[flyerKey],
        [fieldName]: value,
      },
    }));
  }, []);

  const handleMetricChange = useCallback((flyerKey, metricIndex, fieldName, value) => {
    setFlyers((currentFlyers) => ({
      ...currentFlyers,
      [flyerKey]: {
        ...currentFlyers[flyerKey],
        metrics: updateArrayEntry(currentFlyers[flyerKey].metrics, metricIndex, fieldName, value),
      },
    }));
  }, []);

  const handleBenefitChange = useCallback((flyerKey, benefitIndex, fieldName, value) => {
    setFlyers((currentFlyers) => ({
      ...currentFlyers,
      [flyerKey]: {
        ...currentFlyers[flyerKey],
        benefits: updateArrayEntry(currentFlyers[flyerKey].benefits, benefitIndex, fieldName, value),
      },
    }));
  }, []);

  const handleProcessChange = useCallback((flyerKey, processIndex, value) => {
    setFlyers((currentFlyers) => ({
      ...currentFlyers,
      [flyerKey]: {
        ...currentFlyers[flyerKey],
        process: updateStringEntry(currentFlyers[flyerKey].process, processIndex, value),
      },
    }));
  }, []);

  const handleReset = useCallback(() => {
    setFlyers(cloneDefaultFlyers());
  }, []);

  const renderedFlyers = useMemo(() => FLYER_KEYS.map((flyerKey) => flyers[flyerKey]), [flyers]);

  return (
    <div className="flyer-studio" data-testid="flyer-studio">
      <FlyerEditorPanel
        activeFlyerKey={activeFlyerKey}
        flyers={flyers}
        canEdit={canEdit}
        onSelectFlyer={setActiveFlyerKey}
        onFieldChange={handleFieldChange}
        onMetricChange={handleMetricChange}
        onBenefitChange={handleBenefitChange}
        onProcessChange={handleProcessChange}
        onReset={handleReset}
      />

      <div className="proposal-staging flyer-preview-staging" data-proposal-canvas="true">
        <div className="proposal-pages flyer-pages" ref={pagesRef} data-testid="proposal-canvas-flyer">
          {renderedFlyers.map((flyer, flyerIndex) => (
            <React.Fragment key={FLYER_KEYS[flyerIndex]}>
              <div className="page-indicator" data-print-hidden="true">
                <span>Seite {flyerIndex + 1} &bull; {flyer.label}</span>
              </div>
              <FlyerPage flyer={flyer} flyerKey={FLYER_KEYS[flyerIndex]} pageNumber={flyerIndex + 1} />
              <div className="page-gap" style={{ height: flyerIndex === renderedFlyers.length - 1 ? '48px' : '32px' }} data-print-hidden="true" />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};